import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX } from "lucide-react";

const SCENES = [
  { img: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/240c01d529a5fb25859e2c5a866e4967a68407b2209bf45275a27e2c16004317.jpeg", k: "s1", pos: "center 42%" },
  { img: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/3d190193ba3c1c3ff972ddd145c352a7ca3d72677d29b69aad9deff0bf707757.jpeg", k: "s2", pos: "center 40%" },
  { img: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/55feab2f1c415c0eb9a123a3d599a910cfd891af834149062d0fcfb35b30cc70.jpeg", k: "s3", pos: "center 46%" },
  { img: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/c8055212843e7574d009c192fa4b1a36cd79376c5572259a7270e8dcc2d5085c.jpeg", k: "s4", pos: "center 42%" },
];
const SCENE_MS = 4400;

export default function FoundersIntro({ title }) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [inView, setInView] = useState(true);
  const audioRef = useRef(null);
  const rootRef = useRef(null);
  const fadeRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SCENES.length), SCENE_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.2 });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  const fadeTo = (target, ms = 900) => {
    const a = audioRef.current;
    if (!a) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    const steps = 30;
    const start = a.volume;
    const step = (target - start) / steps;
    let i = 0;
    fadeRef.current = setInterval(() => {
      i += 1;
      a.volume = Math.min(1, Math.max(0, start + step * i));
      if (i >= steps) {
        clearInterval(fadeRef.current);
        fadeRef.current = null;
        if (target === 0) a.pause();
      }
    }, ms / steps);
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = false;
    if (!muted && inView) {
      a.play().then(() => fadeTo(0.4, 1100)).catch(() => {});
    } else {
      fadeTo(0, 650);
    }
    return () => { if (fadeRef.current) clearInterval(fadeRef.current); };
  }, [muted, inView]);

  // Musique dès la 1ère interaction (l'autoplay sonore est bloqué par les navigateurs).
  useEffect(() => {
    const kick = () => { setMuted(false); };
    window.addEventListener("pointerdown", kick, { once: true });
    window.addEventListener("keydown", kick, { once: true });
    return () => { window.removeEventListener("pointerdown", kick); window.removeEventListener("keydown", kick); };
  }, []);

  const cur = SCENES[idx];
  return (
    <section ref={rootRef} data-testid="founders-intro" className="relative w-full h-[42vh] min-h-[300px] max-h-[460px] overflow-hidden bg-[#050505] border-b border-white/5">
      <style>{`@keyframes fi-kb { from { transform: scale(1.14); } to { transform: scale(1); } }`}</style>

      <AnimatePresence mode="popLayout">
        <motion.div key={idx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2, ease: "easeInOut" }} className="absolute inset-0">
          <div className="absolute inset-0 bg-cover will-change-transform" style={{ backgroundImage: `url(${cur.img})`, backgroundPosition: cur.pos, animation: "fi-kb 6.5s ease-out forwards" }} />
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.5) 45%, rgba(5,5,5,0.2) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-1/3" style={{ background: "linear-gradient(to top, #050505, transparent)" }} />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-14 sm:pb-16">
        <AnimatePresence mode="wait">
          <motion.div key={cur.k} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="max-w-lg">
            <span className="block text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.26em] text-[#C19A6B] mb-1.5">{t(`founders_intro.${cur.k}_eyebrow`)}</span>
            <h2 className="text-lg sm:text-2xl font-light text-white tracking-tight leading-snug" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.7)" }}>{t(`founders_intro.${cur.k}_line`)}</h2>
          </motion.div>
        </AnimatePresence>
        {title && <span className="mt-3 text-xs sm:text-sm font-normal lowercase tracking-[0.18em] text-[#C19A6B] inline-flex items-center gap-2"><span className="w-6 h-px bg-[#C19A6B] inline-block" />{title}</span>}
      </div>

      <div className="absolute bottom-5 right-6 lg:right-12 z-20 flex items-center gap-2">
        {SCENES.map((s, i) => (
          <button key={s.k} onClick={() => setIdx(i)} data-testid={`founders-dot-${i}`} aria-label={`Scène ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${i === idx ? "w-7 bg-[#C19A6B]" : "w-1.5 bg-white/40 hover:bg-white/70"}`} />
        ))}
      </div>

      <button onClick={() => setMuted((m) => !m)} data-testid="founders-sound-toggle"
        className="absolute top-6 right-6 lg:right-12 z-20 inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-white bg-black/40 hover:bg-black/60 border border-white/20 backdrop-blur-sm px-3 py-2 rounded-full transition-colors">
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-[#C19A6B]" />}
        <span className="hidden sm:inline">{muted ? t("founders_intro.music_on") : t("founders_intro.music_off")}</span>
      </button>

      <audio ref={audioRef} src="/founders-theme.mp3" loop preload="none" />
    </section>
  );
}
